import { EmployerEntry } from '@/DTOS/Report/TimeKeeper/EmployerEntry';
import { DownloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Alert, ConfigProvider, DatePicker, Input, Spin } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Dayjs } from 'dayjs';
import { debounce } from 'lodash';
import moment from 'moment';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setTimeKeeperEntries } from '../../contextRedux/TimeKeeperEntry/TimeKeeperEntryStore';
import { AnimateButton } from '../../customComponents/button/CustomButton';
import { CustomTable } from '../../customComponents/table/CustomTable';
import TimeKeeperEntryService from '../../services/timeKeeperEntry/TimeKeeperEntryService';
import { RootState } from '../../store';
import { saveBlobCrossPlatform } from '../../utils/saveBlobCrossPlatform';

const TimeKeeperEntry: FC = () => {
  const dispatch = useDispatch();

  const dates = useSelector((state: RootState) => state.store?.timeKeeperEntry.data);

  const [searchText, setSearchText] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<Dayjs | null>(null);
  const [columns, setColumns] = useState<ColumnsType<any>>([]);
  const [dataSource, setDataSource] = useState<any[]>([]);

  const { Search } = Input;

  const date = selectedMonth ? selectedMonth.format('YYYY-MM') + '-01' : null;

  const {
    data: _dates,
    isFetching: isTimeKeeperEntriesLoading,
    isError: isErrorOnFetchTimeKeeperEntries,
    error: timeKeeperEntriesError,
  } = useQuery({
    queryKey: ['timeKeeperEntries', searchText, date],
    queryFn: () =>
      TimeKeeperEntryService.report({
        termen: searchText,
        date: date,
      }).then((data: any) => {
        dispatch(setTimeKeeperEntries(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const downloadPdf = () => {
    TimeKeeperEntryService.print({
      date: selectedMonth ? selectedMonth.format('YYYY-MM') + '-01' : null,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'file.pdf';

        if (contentDisposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
          }
        }

        return response.blob().then((blob: any) => {
          saveBlobCrossPlatform(blob, filename);
        });
      })
      .catch((error) => {
        console.error('Error processing your request:', error);
      });
  };

  useEffect(() => {
    const processData = (data: EmployerEntry[]) => {
      const groupedData: any = {};

      data.forEach((entry) => {
        const employer = entry.employerName;
        const yearMonth = `${entry.year}-${String(entry.month).padStart(2, '0')}`;

        if (!groupedData[employer]) {
          groupedData[employer] = {};
        }
        if (!groupedData[employer][yearMonth]) {
          groupedData[employer][yearMonth] = {
            entries: [],
            devices: [],
          };
        }

        entry.entries.forEach((e) => {
          groupedData[employer][yearMonth].entries.push({
            date: e.date,
            minTimeOfDay: e.minTimeOfDay,
            maxTimeOfDay: e.maxTimeOfDay,
            duration: e.duration,
            image: e.image,
          });
        });

        // Assuming timeKeeperDevices is part of the main object, modify if necessary
        groupedData[employer][yearMonth].devices = entry?.timeKeeperDevices?.map((d: any) => ({
          id: d.id,
          name: d.name,
        }));
      });

      return groupedData;
    };

    const generateDateColumns = (dateString: string): string[] => {
      const month = moment(dateString, 'YYYY-MM-DD');
      const startOfMonth = month.clone().startOf('month');
      const endOfMonth = month.clone().endOf('month');
      const dates: string[] = [];
      let current = startOfMonth.clone();

      while (current.isSameOrBefore(endOfMonth)) {
        dates.push(current.format('YYYY-MM-DD'));
        current = current.add(1, 'day');
      }

      return dates;
    };

    const createColumns = (dates: string[]): ColumnsType<any> => {
      const columns: ColumnsType<any> = [
        {
          title: 'Personal',
          dataIndex: 'employer',
          key: 'employer',
          fixed: 'left',
          width: 200,
        },
        {
          title: 'Camere',
          dataIndex: 'cameras',
          key: 'cameras',
          width: 200,
          render: (cameras: string) => (cameras ? cameras : '-'),
        },
      ];

      dates.forEach((date) => {
        columns.push({
          title: date,
          dataIndex: date,
          key: date,
          width: 150,
          render: (times: string) => (times ? times : '-'),
        });
      });

      return columns;
    };

    const createDataSource = (groupedData: any) => {
      const dataSource: any = [];

      Object.keys(groupedData).forEach((employer) => {
        const data: any = { employer };

        let hasEntries = false;
        Object.keys(groupedData[employer]).forEach((yearMonth) => {
          groupedData[employer][yearMonth].entries.forEach((entry: any) => {
            const date = moment(entry.date, 'YYYY-MM-DD').format('YYYY-MM-DD');
            if (entry.minTimeOfDay && entry.maxTimeOfDay) {
              hasEntries = true;
              data[
                date
              ] = `Ora Intrare: ${entry.minTimeOfDay}, Ora Iesire: ${entry.maxTimeOfDay}, Durata: ${entry.duration}`;
            }
          });

          const devices = groupedData[employer][yearMonth].devices;
          if (devices?.length > 0) {
            data['cameras'] = devices?.map((device: any) => device.name).join(', ');
          }
        });

        if (hasEntries) {
          dataSource.push(data);
        }
      });

      return dataSource;
    };

    const groupedData = processData(dates);
    const datesList = generateDateColumns(selectedMonth ? selectedMonth.format('YYYY-MM-DD') : '');
    setColumns(createColumns(datesList));
    setDataSource(createDataSource(groupedData));
  }, [dates]);

  const debouncedSetSearchText = useCallback(
    debounce((value: string) => {
      setSearchText(value);
    }, 1000),
    []
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSetSearchText(e.target.value);
  };

  const handleMonthChange = (date: Dayjs | null) => {
    setSelectedMonth(date);
  };

  if (isTimeKeeperEntriesLoading) {
    return <Spin size='large' className='spinner' />;
  }

  if (isErrorOnFetchTimeKeeperEntries) {
    return <Alert message={timeKeeperEntriesError.message} type='error' />;
  }

  return (
    <>
      <h1 style={{ marginBottom: 40 }}>Raport Personal</h1>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '16px',
          marginBottom: '10px',
        }}
      >
        <div
          style={{
            width: '30%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Search
            placeholder='Search...'
            allowClear
            onChange={handleSearch}
            defaultValue={searchText}
            style={{ marginBottom: 10, width: '100%' }}
          />
          <ConfigProvider
            theme={{
              components: {
                DatePicker: {
                  cellHoverBg: '#1677ff',
                },
              },
            }}
          >
            <DatePicker picker='month' value={selectedMonth} onChange={handleMonthChange} style={{ width: '100%' }} />
          </ConfigProvider>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <AnimateButton type='primary' onClick={() => downloadPdf()}>
            <DownloadOutlined />
            PDF
          </AnimateButton>
        </div>
      </div>

      <CustomTable
        initialDataSource={dataSource}
        pagination={false}
        rowKey='employer'
        className='table'
        columns={columns}
      />
    </>
  );
};

export default TimeKeeperEntry;
